import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { X, ChevronDown, Download } from 'lucide-react';
import * as echarts from 'echarts';
import { StationService, StationInfo, StationDataPoint } from '../../services/stationService';
import { ForecastService } from '../../api/forecasting';
import { useDemo } from '../demo/DemoProvider';
import { supabase } from '../auth/AuthProvider';

import { useTheme } from '../../theme/ThemeContext';
import { useTimezone } from '../../contexts/useTimezone';
import { buildTimeSeriesChartConfig } from '../common/chartConfig';
import { FORECAST_MODELS } from '../../common/static-data/models';
import { getModelColor } from '../../pages/forecast/utils/colorUtils';

// TIMEZONE HANDLING:
// Both station observations and forecast data timestamps are normalized to UTC by appending 'Z'
// if not already timezone-aware. This ensures consistent timezone handling and prevents
// misalignment between observations (potentially in local time) and forecasts (UTC).
// The timezone context then handles display formatting in the user's selected timezone.

// Temperature conversion utility
const kelvinToCelsius = (kelvin: number): number => kelvin - 273.15;

// Station data timestamp normalization - similar to forecast data handling
const normalizeStationTimestamp = (timeStr: string): string => {
  // If timestamp already has timezone info (Z or +hh:mm / -hh:mm), leave as is
  if (/Z$/i.test(timeStr) || /([+-]\d{2}:?\d{2})$/.test(timeStr)) {
    return timeStr;
  }

  // Fallback: append Z for any timestamp without timezone
  return `${timeStr}Z`;
};

// Configuration for variables that need temperature conversion
const TEMPERATURE_VARIABLES = {
  air_temperature_at_height_level_2m: {
    stationVariable: 'air_temperature_at_height_level_2m',
    forecastVariables: ['air_temperature_at_height_level_2m', 'air_temperature_2m'],
  },
  dew_point_temperature_at_height_level_2m: {
    stationVariable: 'dew_point_temperature_at_height_level_2m',
    forecastVariables: ['dew_point_temperature_at_height_level_2m', 'dew_point_temperature_2m'],
  },
};

// Helper function to check if a variable needs temperature conversion
const isTemperatureVariable = (variableId: string): boolean => {
  return variableId in TEMPERATURE_VARIABLES;
};

// Helper function to check if a forecast variable matches a temperature variable
const isForecastTemperatureVariable = (
  selectedVariable: string,
  forecastVariable: string,
): boolean => {
  const tempConfig = TEMPERATURE_VARIABLES[selectedVariable as keyof typeof TEMPERATURE_VARIABLES];
  return tempConfig ? tempConfig.forecastVariables.includes(forecastVariable) : false;
};

interface StationDataModalProps {
  station: StationInfo | null;
  isOpen: boolean;
  onClose: () => void;
}

const VARIABLES = [
  { id: 'air_temperature_at_height_level_2m', label: 'Air Temperature 2m [°C]', unit: '°C' },
  { id: 'wind_speed_at_height_level_10m', label: 'Wind Speed 10m [m/s]', unit: 'm/s' },
];

const OVERLAP_DAYS = 10.0;
const WINDOW_SIZE_DAYS = 15;

const MODELS = [
  { id: 'ept2', label: 'EPT-2' },
  { id: 'ept2_reasoning', label: 'EPT-2 Reasoning' },
  { id: 'ept2_hrrr', label: 'EPT-2 HRRR' },
  { id: 'ept2_early', label: 'EPT-2 Early' },
  { id: 'ept2_e', label: 'EPT-2e' },
  { id: 'ept1_5', label: 'EPT-1.5' },
  { id: 'ept1_5_early', label: 'EPT-1.5 Early' },
  { id: 'aurora', label: 'Microsoft Aurora' },
  { id: 'aifs', label: 'EC AIFS' },
  { id: 'ecmwf_ifs_single', label: 'EC IFS' },
  { id: 'noaa_gfs_single', label: 'NOAA GFS' },
];

const StationDataModal: React.FC<StationDataModalProps> = ({ station, isOpen, onClose }) => {
  const { isDemo } = useDemo();
  const [selectedVariable, setSelectedVariable] = useState('air_temperature_at_height_level_2m');
  const [selectedModels, setSelectedModels] = useState<Set<string>>(new Set([])); // Default to multiple models for comparison
  const [showError, setShowError] = useState(false); // Toggle for showing absolute error
  const [showBest, setShowBest] = useState(false); // Toggle for showing only best performing models
  const [data, setData] = useState<StationDataPoint[]>([]);
  const [forecastData, setForecastData] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [, setForecastLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [, setForecastStatus] = useState<{
    type: 'success' | 'warning' | 'error' | null;
    message: string;
    details?: string;
  }>({ type: null, message: '' });
  const [modelDropdownOpen, setModelDropdownOpen] = useState(false);
  const [isVariableDropdownOpen, setIsVariableDropdownOpen] = useState(false);

  const chartInstance = useRef<echarts.ECharts | null>(null);
  const isInitialized = useRef<boolean>(false);
  const variableDropdownRef = useRef<HTMLDivElement>(null);
  const modelDropdownRef = useRef<HTMLDivElement>(null);

  // OPTIMIZATION: Cache for init times to avoid redundant API calls
  const initTimesCacheRef = useRef<Map<string, { data: any[]; timestamp: number }>>(new Map());
  const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

  // Helper function to get cached init times or fetch new ones
  const getCachedInitTimes = async (
    modelId: string,
    startTime: string,
    endTime: string,
  ): Promise<string[]> => {
    const cacheKey = `${modelId}_${startTime}_${endTime}`;
    const cached = initTimesCacheRef.current.get(cacheKey);
    const now = Date.now();

    if (cached && now - cached.timestamp < CACHE_DURATION) {
      console.log(`Using cached init times for ${modelId}`);
      return cached.data;
    }

    // Models that don't support the overlap endpoint and need fallback to regular init times
    const modelsNeedingFallback = ['ept2_e', 'ept2_hrrr'];

    // Fetch fresh data - wrap in try-catch for models that don't support overlap endpoint
    let availableInitTimes: string[] = [];
    try {
      availableInitTimes = await ForecastService.getAvailableInitTimesWithOverlap(
        modelId,
        startTime,
        endTime,
        OVERLAP_DAYS,
        isDemo,
      );
    } catch (overlapError) {
      console.warn(`Overlap endpoint failed for ${modelId}:`, overlapError);
      // Will fall through to fallback below
    }

    // Fallback: try regular init times if overlap query fails or returns empty
    if (availableInitTimes.length === 0 && modelsNeedingFallback.includes(modelId)) {
      try {
        const fallbackInitTimes = await ForecastService.getAvailableInitTimes(modelId, isDemo);
        availableInitTimes = fallbackInitTimes;
        console.log(`Using fallback init times for ${modelId}: ${fallbackInitTimes.length} times`);
      } catch (fallbackError) {
        console.warn(`${modelId} fallback failed:`, fallbackError);
      }
    }

    // Cache the result
    initTimesCacheRef.current.set(cacheKey, {
      data: availableInitTimes,
      timestamp: now,
    });

    return availableInitTimes;
  };

  const { theme } = useTheme();
  const { formatInUserTimezone } = useTimezone();

  // Variable mapping between station data and forecast data
  // Try both legacy and new variable names for different models
  const getVariableForForecast = (stationVariable: string, forecastData: any) => {
    const possibleMappings = {
      air_temperature_at_height_level_2m: [
        'air_temperature_2m', // Legacy (EPT2)
        'air_temperature_at_height_level_2m', // New format (ICON_GLOBAL, AURORA, AIFS)
      ],
      wind_speed_at_height_level_10m: [
        'wind_speed_10m', // Legacy
        'wind_speed_at_height_level_10m', // New format
      ],
      wind_direction_at_height_level_10m: [
        'wind_direction_10m', // Legacy
        'wind_direction_at_height_level_10m', // New format
      ],
      air_pressure_at_mean_sea_level: [
        'air_pressure_at_mean_sea_level', // Same for both
      ],
      precipitation_amount_sum_1h: [
        'precipitation_amount', // Legacy
        'precipitation_amount_sum_1h', // New format
      ],
    };

    const possibleNames = possibleMappings[stationVariable as keyof typeof possibleMappings] || [];

    // Try each possible variable name and return the first one that exists
    for (const varName of possibleNames) {
      if (forecastData.points?.[0]?.[varName]) {
        return varName;
      }
    }

    return null;
  };

  const fetchStationData = async () => {
    if (!station) return;

    setLoading(true);
    setError(null);

    try {
      const getToken = async () => {
        const { data } = await supabase.auth.getSession();
        const token = data.session?.access_token;
        if (!token) {
          throw new Error('No authentication token available');
        }
        return token;
      };

      const stationService = new StationService(import.meta.env.VITE_API_BASE_URL, getToken);

      // Query a larger range to find what observation data is actually available
      const endTime = new Date();
      const startTime = new Date();
      startTime.setDate(endTime.getDate() - WINDOW_SIZE_DAYS); // Query 30 days to find what's available

      const response = await stationService.getStationData({
        station_ids: [station.station],
        start_time: startTime.toISOString(),
        end_time: endTime.toISOString(),
        variables: [selectedVariable], // Only fetch the selected variable
        limit: 10000,
      });

      if (response.error || !response.data) {
        throw new Error(response.error || 'Failed to fetch station data');
      }

      const stationData = response.data.data;
      setData(stationData);

      // Calculate the actual available date range from the data
      if (stationData.length > 0) {
        const stationTimes = stationData.map((d) => new Date(d.time));
        const actualStartTime = new Date(Math.min(...stationTimes.map((t) => t.getTime())));
        const actualEndTime = new Date(Math.max(...stationTimes.map((t) => t.getTime())));

        // Calculate 15 days BEFORE the last available observation
        const adjustedStartTime = new Date(actualEndTime);
        adjustedStartTime.setDate(actualEndTime.getDate() - 15);

        // Use the adjusted range: 15 days ending at the last available observation
        const finalStartTime = new Date(
          Math.max(adjustedStartTime.getTime(), actualStartTime.getTime()),
        );
        const finalEndTime = actualEndTime;

        console.log('Adjusted date range for forecasts:', {
          original: { start: actualStartTime.toISOString(), end: actualEndTime.toISOString() },
          adjusted: { start: finalStartTime.toISOString(), end: finalEndTime.toISOString() },
          daysCovered: Math.ceil(
            (finalEndTime.getTime() - finalStartTime.getTime()) / (1000 * 60 * 60 * 24),
          ),
        });

        // OPTIMIZATION: Start forecast fetching immediately with adjusted date range
        if (selectedModels.size > 0) {
          fetchForecastData(stationData, finalStartTime, finalEndTime);
        }
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const fetchForecastData = async (
    stationDataPoints?: StationDataPoint[],
    startTime?: Date,
    endTime?: Date,
  ) => {
    // Use provided station data or fall back to current state
    const dataToUse = stationDataPoints || data;
    const startDate =
      startTime || new Date(Math.min(...dataToUse.map((d) => new Date(d.time).getTime())));
    const endDate =
      endTime || new Date(Math.max(...dataToUse.map((d) => new Date(d.time).getTime())));

    if (!station || dataToUse.length === 0 || selectedModels.size === 0) {
      return;
    }

    setForecastLoading(true);
    setForecastStatus({ type: null, message: '' }); // Clear previous status

    try {
      // Calculate station data time range (min/max from all data points)
      const stationTimes = dataToUse.map((d) => new Date(d.time));
      const stationStartTime = new Date(Math.min(...stationTimes.map((t) => t.getTime())));
      const stationEndTime = new Date(Math.max(...stationTimes.map((t) => t.getTime())));

      // OPTIMIZATION: Fetch init times for ALL models in parallel instead of sequentially
      const modelIds = Array.from(selectedModels);
      const initTimePromises = modelIds.map(async (modelId) => {
        try {
          // Use cached init times for better performance with the adjusted date range
          const availableInitTimes = await getCachedInitTimes(
            modelId,
            startDate.toISOString(),
            endDate.toISOString(),
          );

          console.log(`Available init times for ${modelId}:`, availableInitTimes.length);

          if (availableInitTimes && availableInitTimes.length > 0) {
            // Find init times with at least 5 days overlap
            const minOverlapDays = OVERLAP_DAYS;
            const minOverlapMs = minOverlapDays * 24 * 60 * 60 * 1000;

            const viableInitTimes = availableInitTimes
              .map((initTimeStr) => new Date(initTimeStr))
              .map((initTime) => {
                // Calculate forecast period (30 days from init time)
                const forecastEndTime = new Date(initTime.getTime() + 30 * 24 * 60 * 60 * 1000);

                // Calculate overlap using the adjusted date range
                const overlapStart = new Date(Math.max(initTime.getTime(), startDate.getTime()));
                const overlapEnd = new Date(Math.min(forecastEndTime.getTime(), endDate.getTime()));
                const overlapMs = Math.max(0, overlapEnd.getTime() - overlapStart.getTime());
                const overlapDays = overlapMs / (24 * 60 * 60 * 1000);

                return {
                  initTime,
                  forecastEndTime,
                  overlapDays,
                  overlapMs,
                };
              })
              .filter((item) => item.overlapMs >= minOverlapMs)
              .sort((a, b) => b.initTime.getTime() - a.initTime.getTime()); // Sort by most recent first

            // Prefer 00 or 12 UTC runs for consistency across models (main operational runs)
            const mainRuns = viableInitTimes.filter((item) => {
              const hours = item.initTime.getUTCHours();
              return hours === 0 || hours === 12;
            });

            // Use main runs if available, otherwise fall back to any viable init time
            const candidateInitTimes = mainRuns.length > 0 ? mainRuns : viableInitTimes;

            // Take the most recent viable init time for this model
            if (candidateInitTimes.length > 0) {
              const bestInitTime = candidateInitTimes[0];
              const isMainRun =
                bestInitTime.initTime.getUTCHours() === 0 ||
                bestInitTime.initTime.getUTCHours() === 12;
              const result = {
                model: modelId,
                initTime: bestInitTime.initTime.toISOString(),
                overlapDays: bestInitTime.overlapDays,
              };
              console.log(
                `Selected best init time for ${modelId}: ${bestInitTime.initTime.toISOString()} (${bestInitTime.overlapDays.toFixed(1)} days overlap)${isMainRun ? ' [00/12 UTC run]' : ' [off-cycle run]'}`,
              );
              return result;
            }
          }
          return null;
        } catch (error) {
          console.warn(`Failed to fetch init times for model ${modelId}:`, error);
          return null;
        }
      });

      // Wait for all init time queries to complete in parallel
      const initTimeResults = await Promise.all(initTimePromises);
      const bestInitTimeOptions = initTimeResults.filter(Boolean) as {
        model: string;
        initTime: string;
        overlapDays: number;
      }[];

      console.log('Adjusted date range for forecasts:', {
        start: startDate.toISOString(),
        end: endDate.toISOString(),
        daysCovered: Math.ceil((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24)),
      });

      // Check which selected models have no available data
      const modelsWithoutData = Array.from(selectedModels).filter(
        (modelId) => !bestInitTimeOptions.some((option) => option.model === modelId),
      );

      if (modelsWithoutData.length > 0) {
        console.log(`⚠️ Models without available data: ${modelsWithoutData.join(', ')}`);
      }

      if (bestInitTimeOptions.length === 0) {
        setForecastStatus({
          type: 'error',
          message: 'No forecast data available',
          details: `No forecast data found for selected models${modelsWithoutData.length > 0 ? ': ' + modelsWithoutData.join(', ') : ''}. Try selecting different models or time period.`,
        });
        return;
      } else if (modelsWithoutData.length > 0) {
        setForecastStatus({
          type: 'warning',
          message: `${modelsWithoutData.length} model(s) have no data available`,
          details: `${modelsWithoutData.join(', ')} - no initialization times found with sufficient overlap for this time period.`,
        });
      }

      console.log('Best init times selected:', bestInitTimeOptions);

      console.log('Forecast fetch range:', {
        adjustedStart: startDate.toISOString(),
        adjustedEnd: endDate.toISOString(),
        originalStationStart: stationStartTime.toISOString(),
        originalStationEnd: stationEndTime.toISOString(),
      });

      // Fetch forecasts for all best init times across all models
      const forecastPromises = bestInitTimeOptions.map(async (option) => {
        try {
          // Calculate max lead time for this specific init time
          const initTime = new Date(option.initTime);
          const maxLeadTimeHours = Math.max(
            6,
            Math.ceil((stationEndTime.getTime() - initTime.getTime()) / (1000 * 60 * 60)),
          );

          console.log(
            `Fetching ${option.model} forecast: ${option.initTime} (${(maxLeadTimeHours / 24).toFixed(1)} days)`,
          );

          // For ensemble models, request the mean instead of sum
          // Use static model configuration to determine if this is an ensemble model
          const modelConfig = FORECAST_MODELS.find((m) => m.id === option.model);
          const isEnsembleModel =
            modelConfig &&
            'ensembleStats' in modelConfig &&
            modelConfig.ensembleStats &&
            modelConfig.ensembleStats.length > 0;
          const ensembleStats = isEnsembleModel ? ['mean'] : undefined;

          const response = await ForecastService.getInitTimeAtPoint(
            option.model,
            option.initTime,
            station.latitude,
            station.longitude,
            6,
            Math.min(maxLeadTimeHours, 720), // Cap at original 30 days but use observation end as limit
            ensembleStats,
            isDemo,
          );
          if (response?.forecast) {
            // Add model information to the forecast data
            return {
              ...response.forecast,
              model: option.model,
            };
          }
          return null;
        } catch (error) {
          console.warn(
            `Failed to fetch forecast for ${option.model} at ${option.initTime}:`,
            error,
          );
          return null;
        }
      });

      const forecasts = await Promise.all(forecastPromises);
      const validForecasts = forecasts.filter(Boolean);

      console.log(
        `Fetched forecasts: ${validForecasts.length}/${forecastPromises.length} successful`,
      );

      if (validForecasts.length > 0) {
        setForecastData(validForecasts);
        // Only clear status if there were no warnings about missing models
        if (modelsWithoutData.length === 0) {
          setForecastStatus({ type: null, message: '' }); // Clear status on success
        }
      }
    } catch (err: any) {
      // Silent error handling - just log and don't show forecast
      console.warn('Forecast loading failed:', err);
      setForecastStatus({ type: null, message: '' });
    } finally {
      setForecastLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen && station) {
      setForecastStatus({ type: null, message: '' }); // Clear previous status
      fetchStationData();
    }
  }, [isOpen, station, selectedVariable]);

  // Clear forecast data when station changes
  useEffect(() => {
    if (station) {
      setForecastData([]); // Clear previous forecast data when station changes
    }
  }, [station]);

  // Clear forecast data immediately when no models are selected
  useEffect(() => {
    if (selectedModels.size === 0) {
      setForecastData([]);
    }
  }, [selectedModels]);

  // Auto-disable error toggle when no models are selected
  useEffect(() => {
    if (selectedModels.size === 0 && showError) {
      setShowError(false);
    }
  }, [selectedModels.size, showError]);

  // Auto-disable best toggle when less than 2 models are selected
  useEffect(() => {
    if (selectedModels.size < 2 && showBest) {
      setShowBest(false);
    }
  }, [selectedModels.size, showBest]);

  // OPTIMIZATION: Debounced forecast fetching when models change (to avoid excessive API calls)
  useEffect(() => {
    if (data.length > 0 && station && selectedModels.size > 0) {
      const timeoutId = setTimeout(() => {
        fetchForecastData();
      }, 300); // 300ms debounce

      return () => clearTimeout(timeoutId);
    }
  }, [data.length, station, selectedModels]);

  // Forecasts are now fetched automatically when models change, no need for separate effect

  // This effect has been moved to the unified click outside handler below

  const getVariableData = () => {
    const processedData = data
      .filter((point) => {
        const value = (point as any)[selectedVariable];
        return value !== null && value !== undefined && !isNaN(value);
      })
      .map((point) => {
        let value = (point as any)[selectedVariable];

        // Convert temperature from Kelvin to Celsius
        if (isTemperatureVariable(selectedVariable)) {
          value = kelvinToCelsius(value);
        }

        // Normalize timestamp to ensure consistent timezone handling with forecast data
        const normalizedTime = normalizeStationTimestamp(point.time);

        return {
          time: new Date(normalizedTime).getTime(),
          value: value,
        };
      })
      .sort((a, b) => a.time - b.time); // Sort by time

    return processedData;
  };

  const getForecastData = () => {
    if (!forecastData || forecastData.length === 0) return [];

    const allForecastPoints: { time: number; value: number; initTime: string; model: string }[] =
      [];

    // Get the last VALID observation time to filter forecasts (excluding NaN values)
    const validObservations = getVariableData(); // This already filters out NaN values
    const lastValidObsTime =
      validObservations.length > 0 ? Math.max(...validObservations.map((d) => d.time)) : null;

    console.log(
      `Processing forecast data: ${forecastData.length} forecasts for ${selectedVariable}`,
    );

    // Process all available forecasts (we've already selected the best ones)
    forecastData.forEach((forecast) => {
      const { init_time, lead_times, times, model } = forecast;

      console.log(`Processing ${model} forecast:`, {
        initTime: init_time,
        availableVars: Object.keys(forecast.points[0]),
      });

      // Get the correct variable name for this specific forecast
      const forecastVariable = getVariableForForecast(selectedVariable, forecast);
      if (!forecastVariable) {
        console.log(
          `❌ No mapping found for ${model} with station variable ${selectedVariable}. Available variables:`,
          Object.keys(forecast.points[0]),
        );
        return;
      }

      console.log(
        `✅ Found variable mapping for ${model}: ${selectedVariable} -> ${forecastVariable}`,
      );

      const values = forecast.points[0][forecastVariable];

      if (!values || !Array.isArray(values)) {
        console.log(
          `❌ No data for ${model} with variable ${forecastVariable}. Available variables:`,
          Object.keys(forecast.points[0]),
        );
        return;
      }

      console.log(`✅ ${model} has ${values.length} data points for ${forecastVariable}`);

      // Convert init_time + lead_times to actual timestamps
      // Use same normalization approach as station data for consistency
      const normalizedInitTime = normalizeStationTimestamp(init_time);
      const initTime = new Date(normalizedInitTime);

      let timeArray: Date[];
      if (times) {
        // Use times if available - normalize each timestamp
        timeArray = times.map((t: string) => new Date(normalizeStationTimestamp(t)));
      } else if (lead_times) {
        // Convert lead_times to actual times
        timeArray = lead_times.map((leadTime: number) => {
          return new Date(initTime.getTime() + leadTime * 60 * 60 * 1000);
        });
      } else {
        return;
      }

      // Filter forecast points to only include times up to last observation
      const forecastPoints = timeArray.map((time, index) => {
        let value = values[index];

        // Convert temperature from Kelvin to Celsius
        if (isForecastTemperatureVariable(selectedVariable, forecastVariable)) {
          value = kelvinToCelsius(value);
        }

        return {
          time: time.getTime(),
          value: value,
          initTime: init_time, // Store init time for color differentiation
          model: model || 'ept2', // Store model for differentiation
        };
      });

      // Only include forecast points that are within or before the last VALID observation time
      // Subtract 30 minutes to ensure forecasts don't extend beyond observations
      const cutoffTime = lastValidObsTime ? lastValidObsTime - 30 * 60 * 1000 : null;
      const filteredForecastPoints = cutoffTime
        ? forecastPoints.filter((point) => point.time <= cutoffTime)
        : forecastPoints;

      console.log(
        `${model}: Filtered ${forecastPoints.length} -> ${filteredForecastPoints.length} points (up to ${cutoffTime ? new Date(cutoffTime).toISOString() : 'no limit'})`,
        {
          lastValidObservation: lastValidObsTime
            ? new Date(lastValidObsTime).toISOString()
            : 'none',
          cutoffTime: cutoffTime ? new Date(cutoffTime).toISOString() : 'none',
          forecastRange:
            forecastPoints.length > 0
              ? {
                  start: new Date(forecastPoints[0].time).toISOString(),
                  end: new Date(forecastPoints[forecastPoints.length - 1].time).toISOString(),
                }
              : 'none',
          filteredRange:
            filteredForecastPoints.length > 0
              ? {
                  start: new Date(filteredForecastPoints[0].time).toISOString(),
                  end: new Date(
                    filteredForecastPoints[filteredForecastPoints.length - 1].time,
                  ).toISOString(),
                }
              : 'none',
        },
      );

      allForecastPoints.push(...filteredForecastPoints);
    });

    // Filter out invalid points and group by model and init time
    const validPoints = allForecastPoints.filter(
      (point) => point.value !== null && point.value !== undefined && !isNaN(point.value),
    );

    // Group by model and init time first, then deduplicate within each group
    const groupedByModelAndInitTime = validPoints.reduce(
      (groups, point) => {
        const key = `${point.model}_${point.initTime}`;
        if (!groups[key]) {
          groups[key] = [];
        }
        groups[key].push(point);
        return groups;
      },
      {} as Record<string, typeof validPoints>,
    );

    // Deduplicate within each model-init time group (in case of duplicate timestamps within same forecast)
    const deduplicatedPoints: { time: number; value: number; initTime: string; model: string }[] =
      [];

    Object.entries(groupedByModelAndInitTime).forEach(([, points]) => {
      const timeMap = new Map<number, { value: number; initTime: string; model: string }>();

      points.forEach((point) => {
        // Keep the last value if there are duplicates within the same model-init time
        timeMap.set(point.time, {
          value: point.value,
          initTime: point.initTime,
          model: point.model,
        });
      });

      // Add all points from this model-init time
      timeMap.forEach((data, time) => {
        deduplicatedPoints.push({
          time,
          value: data.value,
          initTime: data.initTime,
          model: data.model,
        });
      });
    });

    console.log('getForecastData result:', {
      totalPoints: deduplicatedPoints.length,
      uniqueModels: [...new Set(deduplicatedPoints.map((p) => p.model))],
      uniqueInitTimes: [...new Set(deduplicatedPoints.map((p) => p.initTime))],
      pointsByModelAndInitTime: deduplicatedPoints.reduce(
        (acc, point) => {
          const key = `${point.model}_${point.initTime}`;
          acc[key] = (acc[key] || 0) + 1;
          return acc;
        },
        {} as Record<string, number>,
      ),
    });

    const sortedPoints = deduplicatedPoints.sort((a, b) => a.time - b.time);

    console.log('Final forecast points being returned:', {
      totalPoints: sortedPoints.length,
      modelBreakdown: sortedPoints.reduce(
        (acc, point) => {
          acc[point.model] = (acc[point.model] || 0) + 1;
          return acc;
        },
        {} as Record<string, number>,
      ),
      samplePoints: sortedPoints.slice(0, 5).map((p) => ({
        model: p.model,
        time: new Date(p.time).toISOString(),
        value: p.value,
      })),
    });

    return sortedPoints;
  };

  const rawChartData = useMemo(() => getVariableData(), [data, selectedVariable]);
  const chartForecastData = useMemo(() => getForecastData(), [forecastData, selectedVariable]);
  const selectedVar = VARIABLES.find((v) => v.id === selectedVariable);

  // Filter observations to only show dates where there's forecast data overlap
  const chartData = useMemo(() => {
    if (chartForecastData.length === 0 || selectedModels.size === 0) {
      // No forecast data - show all observations
      return rawChartData;
    }

    // Find the earliest forecast time across all models
    const forecastTimes = chartForecastData.map((p) => p.time);
    const earliestForecastTime = Math.min(...forecastTimes);

    // Filter observations to only include times >= earliest forecast time
    const filteredData = rawChartData.filter((obs) => obs.time >= earliestForecastTime);

    console.log('Filtered observations to forecast overlap:', {
      originalCount: rawChartData.length,
      filteredCount: filteredData.length,
      earliestForecast: new Date(earliestForecastTime).toISOString(),
    });

    return filteredData;
  }, [rawChartData, chartForecastData, selectedModels.size]);

  // Linear interpolation function
  const linearInterpolate = (x: number, x1: number, y1: number, x2: number, y2: number): number => {
    if (x1 === x2) return y1;
    return y1 + ((y2 - y1) * (x - x1)) / (x2 - x1);
  };

  // Calculate error data for ranking models (always calculated, displayed only when showError is true)
  const getErrorData = () => {
    if (chartData.length === 0 || chartForecastData.length === 0) {
      return [];
    }

    console.log('Calculating error data with linear interpolation');

    // Group forecast data by model
    const forecastsByModel = chartForecastData.reduce(
      (acc, point) => {
        if (!acc[point.model]) {
          acc[point.model] = [];
        }
        acc[point.model].push(point);
        return acc;
      },
      {} as Record<string, typeof chartForecastData>,
    );

    // Sort observations by time for consistent processing
    const sortedObservations = chartData.slice().sort((a, b) => a.time - b.time);

    const errorData: { time: number; value: number; model: string; initTime: string }[] = [];

    // For each model, interpolate forecasts to observation times
    Object.entries(forecastsByModel).forEach(([model, forecastPoints]) => {
      // Sort forecast points by time
      const sortedForecasts = forecastPoints.slice().sort((a, b) => a.time - b.time);

      // Filter out forecasts within first 6 hours of init time
      const filteredForecasts = sortedForecasts.filter((forecast) => {
        const initTime = new Date(forecast.initTime).getTime();
        const forecastTime = forecast.time;
        const timeDiffHours = (forecastTime - initTime) / (1000 * 60 * 60);
        return timeDiffHours >= 6; // Exclude first 6 hours
      });

      console.log(`Processing ${model}:`, {
        forecastCount: sortedForecasts.length,
        filteredCount: filteredForecasts.length,
        forecastTimeRange:
          filteredForecasts.length > 0
            ? {
                start: new Date(filteredForecasts[0].time).toISOString(),
                end: new Date(filteredForecasts[filteredForecasts.length - 1].time).toISOString(),
              }
            : null,
      });

      // For each observation, interpolate the forecast value at that time
      sortedObservations.forEach((obs) => {
        // Find the forecast points that bracket this observation time
        let beforeIdx = -1;
        let afterIdx = -1;

        for (let i = 0; i < filteredForecasts.length; i++) {
          if (filteredForecasts[i].time <= obs.time) {
            beforeIdx = i;
          } else {
            afterIdx = i;
            break;
          }
        }

        let interpolatedValue: number | null = null;
        let initTime = '';

        if (beforeIdx >= 0 && afterIdx >= 0) {
          // Interpolate between two forecast points
          const before = filteredForecasts[beforeIdx];
          const after = filteredForecasts[afterIdx];
          interpolatedValue = linearInterpolate(
            obs.time,
            before.time,
            before.value,
            after.time,
            after.value,
          );
          initTime = before.initTime; // Use the earlier forecast's init time
        } else if (beforeIdx >= 0 && beforeIdx === filteredForecasts.length - 1) {
          // Observation is after the last forecast point - use last forecast value (no extrapolation)
          const timeDiff = obs.time - filteredForecasts[beforeIdx].time;
          if (timeDiff <= 3 * 60 * 60 * 1000) {
            // Only if within 3 hours
            interpolatedValue = filteredForecasts[beforeIdx].value;
            initTime = filteredForecasts[beforeIdx].initTime;
          }
        } else if (afterIdx >= 0 && afterIdx === 0) {
          // Observation is before the first forecast point - use first forecast value (no extrapolation)
          const timeDiff = filteredForecasts[afterIdx].time - obs.time;
          if (timeDiff <= 3 * 60 * 60 * 1000) {
            // Only if within 3 hours
            interpolatedValue = filteredForecasts[afterIdx].value;
            initTime = filteredForecasts[afterIdx].initTime;
          }
        }

        if (interpolatedValue !== null) {
          const error = Math.abs(obs.value - interpolatedValue);
          errorData.push({
            time: obs.time, // Use observation time
            value: error,
            model: model,
            initTime: initTime,
          });
        }
      });
    });

    console.log('Error data calculated with interpolation:', {
      totalErrors: errorData.length,
      modelBreakdown: errorData.reduce(
        (acc, point) => {
          acc[point.model] = (acc[point.model] || 0) + 1;
          return acc;
        },
        {} as Record<string, number>,
      ),
    });

    return errorData.sort((a, b) => a.time - b.time);
  };

  const errorData = useMemo(() => getErrorData(), [chartData, chartForecastData]);

  // Calculate best performing models based on average error (always calculated when error data exists)
  const getBestModels = () => {
    if (errorData.length === 0) {
      return new Set<string>();
    }

    // Calculate average error for each model
    const modelErrors = errorData.reduce(
      (acc, point) => {
        if (!acc[point.model]) {
          acc[point.model] = { total: 0, count: 0 };
        }
        acc[point.model].total += point.value;
        acc[point.model].count += 1;
        return acc;
      },
      {} as Record<string, { total: number; count: number }>,
    );

    const modelAverages = Object.entries(modelErrors).map(([model, stats]) => ({
      model,
      avgError: stats.total / stats.count,
    }));

    console.log('Model average errors:', modelAverages);

    if (modelAverages.length === 0) {
      return new Set<string>();
    }

    // Find the minimum average error
    const minAvgError = Math.min(...modelAverages.map((m) => m.avgError));

    // Get all models with the minimum error (in case of ties)
    const bestModels = modelAverages
      .filter((m) => Math.abs(m.avgError - minAvgError) < 0.001) // Small epsilon for floating point comparison
      .map((m) => m.model);

    console.log('Best performing models:', { minAvgError, bestModels });

    return new Set(bestModels);
  };

  const bestModels = useMemo(() => getBestModels(), [errorData]);

  // Chart control handlers
  const downloadChart = useCallback(() => {
    if (!chartInstance.current) return;

    const selectedVar = VARIABLES.find((v) => v.id === selectedVariable);
    const varLabel = selectedVar?.label || selectedVariable;
    const filename = `station-data-${varLabel.toLowerCase().replace(/\s+/g, '-')}-${station?.station || 'unknown'}.png`;

    const url = chartInstance.current.getDataURL({
      type: 'png',
      pixelRatio: 2,
      backgroundColor: theme === 'dark' ? '#1F2937' : '#FFFFFF',
    });

    const link = document.createElement('a');
    link.download = filename;
    link.href = url;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }, [selectedVariable, station, theme]);

  // Simplified chart initialization - only dispose on modal close
  useEffect(() => {
    if (!isOpen && chartInstance.current) {
      chartInstance.current.dispose();
      chartInstance.current = null;
      isInitialized.current = false;
    }
  }, [isOpen]);

  // Use callback ref to capture the DOM element and initialize chart
  const chartRefCallback = useCallback((node: HTMLDivElement | null) => {
    if (node && !isInitialized.current) {
      // Initialize chart only once when DOM element becomes available
      chartInstance.current = echarts.init(node, undefined, {
        renderer: 'canvas',
      });
      isInitialized.current = true;
    }
  }, []);

  // Simple direct chart creation and update
  useEffect(() => {
    if (!isOpen || !chartInstance.current || !selectedVar) {
      return;
    }

    // Determine which data to show
    const shouldShowData = showError
      ? errorData.length > 0
      : chartData.length > 0 || chartForecastData.length > 0;

    if (!shouldShowData) {
      return;
    }

    // Create new chart data using the shared chart configuration
    try {
      const isDark = theme === 'dark';
      const axisColor = isDark ? '#D1D5DB' : '#6B7280';

      // Calculate y-axis range based on display mode
      let allValues: number[] = [];

      if (showError) {
        // Error mode: use error data only
        allValues = errorData.map((d) => d.value);
      } else {
        // Normal mode: combine observations and forecasts
        allValues = [...chartData.map((d) => d.value), ...chartForecastData.map((d) => d.value)];
      }

      if (allValues.length === 0) return;

      const minVal = Math.min(...allValues);
      const maxVal = Math.max(...allValues);
      const padding = (maxVal - minVal) * 0.1 || 1;

      // Determine interval based on variable type
      const isTemperature = selectedVar.unit === '°C';
      const isWindSpeed = selectedVar.unit === 'm/s';
      const interval = isTemperature ? 5 : isWindSpeed ? 4 : 1;

      // Round min/max to multiples of interval
      let adjustedMin = Math.floor((minVal - padding) / interval) * interval;
      const adjustedMax = Math.ceil((maxVal + padding) / interval) * interval;

      // For error mode, always start from 0 (errors are absolute values)
      if (showError) {
        adjustedMin = 0;
      }
      // For wind speed, ensure minimum is not less than 0
      else if (isWindSpeed && adjustedMin < 0) {
        adjustedMin = 0;
      }

      const yAxisRange = {
        min: adjustedMin,
        max: adjustedMax,
      };

      // Build series array
      const series: any[] = [];
      const sortedModels: string[] = [];

      // Adjust line width based on number of selected models for better visibility
      const numModels = selectedModels.size;
      const lineWidth = numModels >= 6 ? 2 : numModels >= 4 ? 2.25 : 2.5;
      const emphasizedWidth = numModels >= 6 ? 3 : 4;
      const lineOpacity = numModels >= 6 ? 0.85 : 1;

      if (showError) {
        // Error mode: show error lines for each model
        // Filter by selected models first
        let filteredErrorData = errorData.filter((point) => selectedModels.has(point.model));

        // Then filter by best models if enabled
        if (showBest && bestModels.size > 0) {
          filteredErrorData = filteredErrorData.filter((point) => bestModels.has(point.model));
        }

        const errorsByModel = filteredErrorData.reduce(
          (groups, point) => {
            if (!groups[point.model]) {
              groups[point.model] = [];
            }
            groups[point.model].push([point.time, point.value]);
            return groups;
          },
          {} as Record<string, [number, number][]>,
        );

        let errorColorIndex = 0;

        Object.entries(errorsByModel).forEach(([model, dataPoints]) => {
          // Use consistent color scheme from forecast page
          const color = getModelColor(
            model,
            errorColorIndex++,
            theme === 'dark' ? 'dark' : 'light',
          );
          const modelLabel = MODELS.find((m) => m.id === model)?.label || model.toUpperCase();

          sortedModels.push(`${modelLabel} Error`);

          series.push({
            type: 'line',
            name: `${modelLabel} Error`,
            data: dataPoints.sort((a, b) => a[0] - b[0]),
            lineStyle: {
              color: color,
              width: lineWidth,
              type: 'solid',
              opacity: lineOpacity,
            },
            itemStyle: { color: color, opacity: lineOpacity },
            symbol: 'none',
            smooth: false,
            emphasis: {
              focus: 'series',
              lineStyle: { width: emphasizedWidth, opacity: 1 },
            },
          });
        });
      } else {
        // Normal mode: show observations and forecasts
        // Process forecast data series first
        if (chartForecastData.length > 0) {
          // Filter forecast data by selected models first
          let filteredForecastData = chartForecastData.filter((point) =>
            selectedModels.has(point.model),
          );

          // Then filter by best models if enabled
          if (showBest && bestModels.size > 0) {
            filteredForecastData = filteredForecastData.filter((point) =>
              bestModels.has(point.model),
            );
          }

          // Group forecast points by model and init time
          const groupedByModelAndInitTime = filteredForecastData.reduce(
            (groups, point) => {
              const key = `${point.model}|||${point.initTime}`; // Use ||| as separator to avoid underscore conflicts
              if (!groups[key]) {
                groups[key] = [];
              }
              groups[key].push([point.time, point.value]);
              return groups;
            },
            {} as Record<string, [number, number][]>,
          );

          let colorIndex = 0;

          Object.entries(groupedByModelAndInitTime).forEach(([key, dataPoints]) => {
            const [model] = key.split('|||'); // Split using the new separator

            // Use consistent color scheme from forecast page
            const color = getModelColor(model, colorIndex++, theme === 'dark' ? 'dark' : 'light');
            const lineType = 'solid'; // Use solid lines for better visibility

            const modelLabel = MODELS.find((m) => m.id === model)?.label || model.toUpperCase();
            const seriesName = modelLabel;

            sortedModels.push(seriesName);

            series.push({
              type: 'line',
              name: seriesName,
              data: dataPoints.sort((a, b) => a[0] - b[0]),
              lineStyle: {
                color: color,
                width: lineWidth,
                type: lineType,
                opacity: lineOpacity,
              },
              itemStyle: { color: color, opacity: lineOpacity },
              symbol: 'none',
              smooth: false,
              emphasis: {
                focus: 'series',
                lineStyle: { width: emphasizedWidth, opacity: 1 },
              },
            });
          });
        }

        // Add station data series (observations) AFTER forecasts for proper legend ordering (first position)
        if (chartData.length > 0) {
          const observationsColor = isDark ? '#D1D5DB' : '#4B5563'; // Light grey in dark mode, dark grey in light mode
          sortedModels.unshift('Observations'); // Add to beginning for legend ordering
          series.push({
            type: 'line',
            name: 'Observations',
            data: chartData.map((d) => [d.time, d.value]),
            lineStyle: { color: observationsColor, width: lineWidth, opacity: lineOpacity },
            itemStyle: { color: observationsColor, opacity: lineOpacity },
            symbol: 'circle',
            symbolSize: numModels >= 6 ? 3 : 4,
            smooth: false,
            emphasis: {
              focus: 'series',
              lineStyle: { width: emphasizedWidth, opacity: 1 },
            },
          });
        }
      }

      // Use the shared chart configuration
      const variable = {
        displayUnit: showError ? `Error (${selectedVar.unit})` : selectedVar.unit,
        label: selectedVar.label,
      };

      const option = buildTimeSeriesChartConfig({
        isDark,
        axisColor,
        sortedModels,
        variable,
        yAxisRange,
        series,
        formatInUserTimezone,
        showDataZoom: true,
        showLegend: series.length > 1,
        observationName:
          !showError && chartData.length > 0 && selectedModels.size > 0
            ? 'Observations'
            : undefined,
        yAxisInterval: interval,
      });

      chartInstance.current.setOption(option, {
        notMerge: true,
        lazyUpdate: false,
        silent: false,
      });

      // Ensure chart resizes to container after setting options
      setTimeout(() => {
        if (chartInstance.current) {
          chartInstance.current.resize();
        }
      }, 0);
    } catch (error) {
      console.error('Chart creation error:', error);
    }
  }, [
    isOpen,
    chartData,
    chartForecastData,
    selectedVar,
    theme,
    showError,
    errorData,
    showBest,
    bestModels,
    formatInUserTimezone,
    selectedModels,
  ]);

  // Handle window resize for responsiveness
  useEffect(() => {
    const handleResize = () => {
      if (chartInstance.current) {
        chartInstance.current.resize();
      }
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // Handle click outside for dropdowns
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;

      if (
        isVariableDropdownOpen &&
        variableDropdownRef.current &&
        !variableDropdownRef.current.contains(target)
      ) {
        setIsVariableDropdownOpen(false);
      }

      if (
        modelDropdownOpen &&
        modelDropdownRef.current &&
        !modelDropdownRef.current.contains(target)
      ) {
        setModelDropdownOpen(false);
      }
    };

    if (modelDropdownOpen || isVariableDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [modelDropdownOpen, isVariableDropdownOpen]);

  // Clean up on unmount
  useEffect(() => {
    return () => {
      if (chartInstance.current) {
        chartInstance.current.dispose();
        chartInstance.current = null;
      }
      isInitialized.current = false;
    };
  }, []);

  if (!isOpen || !station) return null;

  const isDark = theme === 'dark';

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4 z-50">
      <div
        className={`h-80 ${
          isDark ? 'bg-gray-800' : 'bg-white'
        } shadow-sm border rounded-xl ${isDark ? 'border-gray-700' : 'border-gray-200'}`}
      >
        {/* Header */}
        <div className="p-3">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
              📊 {station.name}
            </div>
            <div className="flex flex-wrap items-center gap-3 sm:gap-3">
              {/* Variable Selector */}
              <div className="flex items-center gap-2" ref={variableDropdownRef}>
                <label className="text-xs font-medium text-gray-600 dark:text-gray-400 whitespace-nowrap hidden sm:inline">
                  Variable
                </label>
                <div className="relative">
                  <button
                    onClick={() => setIsVariableDropdownOpen(!isVariableDropdownOpen)}
                    className="flex items-center gap-2 px-2 sm:px-3 py-1 text-xs bg-gray-100 dark:bg-gray-800 rounded-md hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100 justify-between whitespace-nowrap w-42 sm:w-46"
                  >
                    <span className="truncate">
                      {VARIABLES.find((v) => v.id === selectedVariable)?.label || selectedVariable}
                    </span>
                    <ChevronDown
                      className={`h-3 w-3 transition-transform flex-shrink-0 ${isVariableDropdownOpen ? 'rotate-180' : ''}`}
                    />
                  </button>

                  {isVariableDropdownOpen && (
                    <div className="absolute bottom-full mb-1 left-0 min-w-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md shadow-lg z-50 max-h-60 overflow-y-auto">
                      {VARIABLES.map((variable) => (
                        <button
                          key={variable.id}
                          onClick={() => {
                            setSelectedVariable(variable.id);
                            setIsVariableDropdownOpen(false);
                          }}
                          className={`w-full text-left px-3 py-2 text-xs hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors ${
                            variable.id === selectedVariable
                              ? 'bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-gray-100'
                              : 'text-gray-600 dark:text-gray-300'
                          }`}
                        >
                          <span className="truncate block">{variable.label}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Model Selector */}
              <div className="flex items-center gap-2" ref={modelDropdownRef}>
                <label className="text-xs font-medium text-gray-600 dark:text-gray-400 whitespace-nowrap hidden sm:inline">
                  Models
                </label>
                <div className="relative">
                  <button
                    onClick={() => setModelDropdownOpen(!modelDropdownOpen)}
                    className="flex items-center gap-2 px-2 sm:px-3 py-1 text-xs bg-gray-100 dark:bg-gray-800 rounded-md hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100 justify-between whitespace-nowrap w-28 sm:w-32"
                  >
                    <span className="truncate">
                      {selectedModels.size === 0
                        ? 'Select models...'
                        : selectedModels.size === 1
                          ? MODELS.find((m) => m.id === Array.from(selectedModels)[0])?.label ||
                            Array.from(selectedModels)[0]
                          : `${selectedModels.size} models`}
                    </span>
                    <ChevronDown
                      className={`h-3 w-3 transition-transform flex-shrink-0 ${modelDropdownOpen ? 'rotate-180' : ''}`}
                    />
                  </button>

                  {modelDropdownOpen && (
                    <div
                      className="absolute bottom-full mb-1 left-0 min-w-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md shadow-lg z-50 max-h-60 overflow-y-auto"
                      onClick={(e) => e.stopPropagation()} // Prevent dropdown from closing when clicking inside
                    >
                      {MODELS.map((model) => {
                        const isSelected = selectedModels.has(model.id);

                        return (
                          <button
                            key={model.id}
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();

                              const newSelected = new Set(selectedModels);
                              if (isSelected) {
                                newSelected.delete(model.id);
                              } else {
                                newSelected.add(model.id);
                              }
                              setSelectedModels(newSelected);
                              // Keep existing forecast data visible while new data loads
                            }}
                            className={`w-full text-left px-3 py-2 text-xs hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors ${
                              isSelected
                                ? 'bg-pink-50 dark:bg-pink-900/20 text-pink-600 dark:text-pink-400 font-medium'
                                : 'text-gray-600 dark:text-gray-300'
                            }`}
                          >
                            <div className="flex items-center justify-between">
                              <span className="truncate">{model.label}</span>
                              <div className="flex items-center gap-1">
                                {isSelected && (
                                  <div className="w-2 h-2 rounded-full ml-1 flex-shrink-0 bg-pink-500" />
                                )}
                              </div>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>

              {/* Error toggle */}
              <div className="flex items-center gap-1 relative group">
                <span
                  className={`text-xs font-medium transition-colors duration-200 hidden sm:inline ${
                    selectedModels.size === 0
                      ? 'text-gray-400 dark:text-gray-500'
                      : 'text-gray-600 dark:text-gray-300'
                  }`}
                >
                  Error
                </span>
                <button
                  onClick={() => setShowError(!showError)}
                  disabled={selectedModels.size === 0}
                  className={`relative w-8 h-4 rounded-full transition-all duration-300 focus:outline-none ${
                    showError
                      ? 'bg-red-500'
                      : selectedModels.size > 0
                        ? 'bg-gray-300 dark:bg-gray-600'
                        : 'bg-gray-200 dark:bg-gray-700 opacity-40 cursor-not-allowed'
                  }`}
                >
                  <div
                    className={`absolute w-3 h-3 bg-white rounded-full shadow-sm transform transition-all duration-300 top-0.5 ${
                      showError ? 'translate-x-4' : 'translate-x-0.5'
                    }`}
                  />
                </button>
                {/* Custom tooltip for disabled state */}
                {selectedModels.size === 0 && (
                  <div className="absolute bottom-full mb-2 left-1/2 transform -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-opacity duration-150 pointer-events-none z-50">
                    <div className="bg-gray-900 dark:bg-gray-700 text-white text-xs px-3 py-2 rounded-lg shadow-lg whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <span className="text-red-400">🔍</span>
                        Select at least 1 model for error analysis
                      </div>
                      <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900 dark:border-t-gray-700"></div>
                    </div>
                  </div>
                )}
              </div>

              {/* Best models toggle */}
              <div className="flex items-center gap-1 relative group">
                <span
                  className={`text-xs font-medium transition-colors duration-200 hidden sm:inline ${
                    selectedModels.size < 2
                      ? 'text-gray-400 dark:text-gray-500'
                      : 'text-gray-600 dark:text-gray-300'
                  }`}
                >
                  Best
                </span>
                <button
                  onClick={() => setShowBest(!showBest)}
                  disabled={selectedModels.size < 2}
                  className={`relative w-8 h-4 rounded-full transition-all duration-300 focus:outline-none ${
                    showBest
                      ? 'bg-green-500'
                      : selectedModels.size >= 2
                        ? 'bg-gray-300 dark:bg-gray-600'
                        : 'bg-gray-200 dark:bg-gray-700 opacity-40 cursor-not-allowed'
                  }`}
                >
                  <div
                    className={`absolute w-3 h-3 bg-white rounded-full shadow-sm transform transition-all duration-300 top-0.5 ${
                      showBest ? 'translate-x-4' : 'translate-x-0.5'
                    }`}
                  />
                </button>
                {/* Custom tooltip for disabled state */}
                {selectedModels.size < 2 && (
                  <div className="absolute bottom-full mb-2 left-1/2 transform -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-opacity duration-150 pointer-events-none z-50">
                    <div className="bg-gray-900 dark:bg-gray-700 text-white text-xs px-3 py-2 rounded-lg shadow-lg whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <span className="text-green-400">🔍</span>
                        Select at least 2 models to compare
                      </div>
                      <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900 dark:border-t-gray-700"></div>
                    </div>
                  </div>
                )}
              </div>

              {/* Chart Controls */}
              <div className="flex items-center gap-1 flex-shrink-0">
                <button
                  onClick={downloadChart}
                  className="p-1.5 text-sm bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 rounded group relative"
                  aria-label="Download Chart"
                  title="Download PNG"
                >
                  <Download className="w-3.5 h-3.5" />
                </button>
                <button
                  onClick={onClose}
                  className="p-1.5 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                  aria-label="Close"
                  title="Close"
                >
                  <X className="w-3.5 h-3.5 text-gray-500 dark:text-gray-400" />
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="relative h-60">
          {error ? (
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="text-sm text-gray-500 dark:text-gray-400">⚠️ {error}</div>
            </div>
          ) : (!data || data.length === 0) &&
            (!forecastData || forecastData.length === 0) &&
            !loading ? (
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="text-sm text-gray-500 dark:text-gray-400">No data available</div>
            </div>
          ) : (
            <div ref={chartRefCallback} className="w-full h-full" />
          )}
        </div>
      </div>
    </div>
  );
};

export default StationDataModal;
